/* eslint-disable no-console */

import dashboardPages from '../03-generated/dashboard-pages.js';

/**
 * FFA Event Dashboard - IndexedDB Runtime Library
 *
 * Runtime state structure:
 * [
 *   {
 *     route: "/category/",
 *     pageType: "PLP",
 *     events: [
 *       {
 *         name: "add-to-cart.plp",
 *         pass: 0,
 *         fail: 0
 *       }
 *     ]
 *   }
 * ]
 */

(function initializeDashboardPagesLibrary(window) {
  window.util = window.util || {};
  window.util.dashboardPages = window.util.dashboardPages || {};

  const DB_NAME = 'FFA_Event_Dashboard_DB';
  const DB_VERSION = 1;
  const STORE_NAME = 'dashboard';
  const DASHBOARD_STATE_KEY = 'dashboardState';

  function cloneDashboardPages() {
    return JSON.parse(JSON.stringify(dashboardPages));
  }

  function openDatabase() {
    return new Promise((resolve, reject) => {
      const request = window.indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        reject(request.error);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };

      request.onsuccess = () => {
        resolve(request.result);
      };
    });
  }

  function getDashboardStateFromDB(db) {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(DASHBOARD_STATE_KEY);

      request.onerror = () => {
        reject(request.error);
      };

      request.onsuccess = () => {
        resolve(request.result || null);
      };
    });
  }

  function saveDashboardStateToDB(db, dashboardState) {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(dashboardState, DASHBOARD_STATE_KEY);

      request.onerror = () => {
        reject(request.error);
      };

      transaction.oncomplete = () => {
        resolve(dashboardState);
      };

      transaction.onerror = () => {
        reject(transaction.error);
      };
    });
  }

  function clearDashboardStore(db) {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onerror = () => {
        reject(request.error);
      };

      request.onsuccess = () => {
        resolve();
      };
    });
  }

  function getCurrentPath(routeOverride) {
    if (routeOverride && typeof routeOverride === 'string') {
      return routeOverride;
    }

    return window.location.pathname;
  }

  function findScorecardForRoute(dashboardState, routeOverride) {
    const currentPath = getCurrentPath(routeOverride);

    return dashboardState.find((scorecard) => {
      return currentPath.includes(scorecard.route);
    });
  }

  async function initializeIndexDB(options = {}) {
    const reset = options.reset === true;
    const shouldLog = options.log !== false;

    const db = await openDatabase();
    const existingState = await getDashboardStateFromDB(db);

    if (existingState && !reset) {
      db.close();

      const result = {
        success: true,
        stateExisted: true,
        reset: false,
        databaseName: DB_NAME,
        storeName: STORE_NAME,
        scorecardsLoaded: existingState.length,
        dashboardState: existingState,
      };

      if (shouldLog) {
        console.log('[FFA Event Dashboard] Existing IndexedDB state found:', result);
      }

      return result;
    }

    if (reset) {
      await clearDashboardStore(db);
    }

    const initialDashboardState = cloneDashboardPages();

    await saveDashboardStateToDB(db, initialDashboardState);

    db.close();

    const result = {
      success: true,
      stateExisted: false,
      reset,
      databaseName: DB_NAME,
      storeName: STORE_NAME,
      scorecardsLoaded: initialDashboardState.length,
      dashboardState: initialDashboardState,
    };

    if (shouldLog) {
      console.log('[FFA Event Dashboard] IndexedDB initialized:', result);
    }

    return result;
  }

  async function resetIndexDB() {
    return initializeIndexDB({
      reset: true,
      log: true,
    });
  }

  async function getDashboardState() {
    const db = await openDatabase();
    const dashboardState = await getDashboardStateFromDB(db);
    db.close();

    return dashboardState;
  }

  async function incrementEventResult(options = {}) {
    const eventName = options.eventName || options.name;
    const resultType = options.result;
    const routeOverride = options.route;
    const shouldLog = options.log !== false;

    if (!eventName || typeof eventName !== 'string') {
      throw new Error('[FFA Event Dashboard] incrementEventResult requires eventName or name.');
    }

    if (resultType !== 'pass' && resultType !== 'fail') {
      throw new Error('[FFA Event Dashboard] result must be "pass" or "fail".');
    }

    const db = await openDatabase();

    let dashboardState = await getDashboardStateFromDB(db);

    if (!dashboardState) {
      dashboardState = cloneDashboardPages();
      await saveDashboardStateToDB(db, dashboardState);
    }

    const scorecard = findScorecardForRoute(dashboardState, routeOverride);

    if (!scorecard) {
      db.close();

      const response = {
        success: false,
        reason: 'route_not_found',
        route: getCurrentPath(routeOverride),
        eventName,
        result: resultType,
        availableRoutes: dashboardState.map((item) => item.route),
        dashboardState,
      };

      if (shouldLog) {
        console.warn('[FFA Event Dashboard] No scorecard found for route.', response);
      }

      return response;
    }

    const eventScore = scorecard.events.find((event) => {
      return event.name === eventName;
    });

    if (!eventScore) {
      db.close();

      const response = {
        success: false,
        reason: 'event_not_found',
        route: scorecard.route,
        pageType: scorecard.pageType,
        eventName,
        result: resultType,
        availableEvents: scorecard.events.map((event) => event.name),
        dashboardState,
      };

      if (shouldLog) {
        console.warn('[FFA Event Dashboard] Event not found on route scorecard.', response);
      }

      return response;
    }

    eventScore[resultType] += 1;

    await saveDashboardStateToDB(db, dashboardState);

    db.close();

    const response = {
      success: true,
      route: scorecard.route,
      pageType: scorecard.pageType,
      eventName,
      result: resultType,
      currentValue: eventScore[resultType],
      eventScore,
      dashboardState,
    };

    if (shouldLog) {
      console.log('[FFA Event Dashboard] Event result incremented:', response);
    }

    return response;
  }

  window.util.dashboardPages = {
    sourceConfig: dashboardPages,

    initializeIndexDB,
    initialiseIndexDB: initializeIndexDB,
    resetIndexDB,
    getDashboardState,
    incrementEventResult,
  };
})(window);
